import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  FileText, File, Download, Eye, Loader2, ShieldCheck, X,
  FolderOpen, Phone, Mail, Globe, AlertTriangle, FileSpreadsheet, FileArchive,
  PenLine, CheckCircle2, RotateCcw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHARE_T = {
  nl: {
    cats: { plannen: 'Plannen', schemas: "Schema's", offerte: 'Offerte', keuring: 'Keuring', facturen: 'Facturen', fotos: "Foto's", fotos_reiniging: "Foto's Reiniging", fotos_installatie: "Foto's Installatie", datasheets: 'Datasheets', andere: 'Andere documenten' },
    ctaText: 'Laat uw zonnepanelen reinigen door ons!', ctaBtn: 'Vraag offerte aan', ctaSending: 'Versturen...',
    heroKicker: 'Uw documenten staan klaar',
    heroWelcome: (name) => `Welkom in uw ${name} Portaal, wij willen u alvast bedanken voor het vertrouwen.`,
    secured: (name) => `Beveiligd gedeeld door ${name}`,
    downloadAll: (n) => `Alles downloaden (${n} bestanden)`,
    noDocs: 'Er staan nog geen documenten in deze map.',
    view: 'Bekijken', download: 'Download', signed: 'Ondertekend', signRequired: 'Handtekening vereist',
    signedBy: (n, d) => `Ondertekend door ${n} op ${d}`, placeSignature: 'Handtekening plaatsen',
    signTitle: 'Offerte ondertekenen', yourName: 'Uw volledige naam', namePh: 'Voornaam Achternaam',
    signature: 'Handtekening', drawHint: 'Teken uw handtekening hierboven met muis of vinger', clear: 'Wissen',
    agree: 'Ik verklaar akkoord te gaan met deze offerte en plaats hierbij mijn digitale handtekening.',
    cancel: 'Annuleren', signBtn: 'Ondertekenen', signError: 'Er ging iets mis, probeer het opnieuw.',
    thanks: 'Bedankt!', signSuccess: 'Uw offerte is succesvol ondertekend. Wij gaan meteen voor u aan de slag. U kunt de ondertekende versie hieronder downloaden.',
    downloadSigned: 'Ondertekende offerte downloaden', close: 'Sluiten',
    cantPreview: 'Dit bestandstype kan niet worden weergegeven — gebruik de downloadknop.',
    footerQ: 'Vragen over deze documenten? Neem gerust contact op.',
    invalidTitle: 'Link niet geldig',
    cleaningError: 'Er ging iets mis. Probeer later opnieuw of neem contact op.',
    project: 'Project',
    locale: 'nl-BE',
  },
  fr: {
    cats: { plannen: 'Plans', schemas: 'Schémas', offerte: 'Devis', keuring: 'Contrôle', facturen: 'Factures', fotos: 'Photos', fotos_reiniging: 'Photos Nettoyage', fotos_installatie: 'Photos Installation', datasheets: 'Fiches techniques', andere: 'Autres documents' },
    ctaText: 'Faites nettoyer vos panneaux solaires par nous !', ctaBtn: 'Demander un devis', ctaSending: 'Envoi...',
    heroKicker: 'Vos documents sont prêts',
    heroWelcome: (name) => `Bienvenue dans votre portail ${name}, nous vous remercions d'avance pour votre confiance.`,
    secured: (name) => `Partagé en toute sécurité par ${name}`,
    downloadAll: (n) => `Tout télécharger (${n} fichiers)`,
    noDocs: "Il n'y a pas encore de documents dans ce dossier.",
    view: 'Voir', download: 'Télécharger', signed: 'Signé', signRequired: 'Signature requise',
    signedBy: (n, d) => `Signé par ${n} le ${d}`, placeSignature: 'Placer la signature',
    signTitle: 'Signer le devis', yourName: 'Votre nom complet', namePh: 'Prénom Nom',
    signature: 'Signature', drawHint: 'Dessinez votre signature ci-dessus avec la souris ou le doigt', clear: 'Effacer',
    agree: "Je déclare accepter ce devis et j'appose par la présente ma signature numérique.",
    cancel: 'Annuler', signBtn: 'Signer', signError: "Une erreur s'est produite, veuillez réessayer.",
    thanks: 'Merci !', signSuccess: 'Votre devis a été signé avec succès. Nous nous mettons immédiatement au travail pour vous. Vous pouvez télécharger la version signée ci-dessous.',
    downloadSigned: 'Télécharger le devis signé', close: 'Fermer',
    cantPreview: "Ce type de fichier ne peut pas être affiché — utilisez le bouton de téléchargement.",
    footerQ: 'Des questions sur ces documents ? Contactez-nous.',
    invalidTitle: 'Lien non valide',
    cleaningError: 'Une erreur est survenue. Réessayez plus tard ou contactez-nous.',
    project: 'Projet',
    locale: 'fr-BE',
  },
  en: {
    cats: { plannen: 'Plans', schemas: 'Diagrams', offerte: 'Quote', keuring: 'Inspection', facturen: 'Invoices', fotos: 'Photos', fotos_reiniging: 'Cleaning Photos', fotos_installatie: 'Installation Photos', datasheets: 'Datasheets', andere: 'Other documents' },
    ctaText: 'Let us clean your solar panels!', ctaBtn: 'Request a quote', ctaSending: 'Sending...',
    heroKicker: 'Your documents are ready',
    heroWelcome: (name) => `Welcome to your ${name} Portal, we would like to thank you in advance for your trust.`,
    secured: (name) => `Securely shared by ${name}`,
    downloadAll: (n) => `Download all (${n} files)`,
    noDocs: 'There are no documents in this folder yet.',
    view: 'View', download: 'Download', signed: 'Signed', signRequired: 'Signature required',
    signedBy: (n, d) => `Signed by ${n} on ${d}`, placeSignature: 'Place signature',
    signTitle: 'Sign quote', yourName: 'Your full name', namePh: 'First name Last name',
    signature: 'Signature', drawHint: 'Draw your signature above with mouse or finger', clear: 'Clear',
    agree: 'I declare that I agree with this quote and hereby place my digital signature.',
    cancel: 'Cancel', signBtn: 'Sign', signError: 'Something went wrong, please try again.',
    thanks: 'Thank you!', signSuccess: 'Your quote has been signed successfully. We will get started for you right away. You can download the signed version below.',
    downloadSigned: 'Download signed quote', close: 'Close',
    cantPreview: 'This file type cannot be displayed — use the download button.',
    footerQ: 'Questions about these documents? Feel free to contact us.',
    invalidTitle: 'Link not valid',
    cleaningError: 'Something went wrong. Try again later or contact us.',
    project: 'Project',
    locale: 'en-GB',
  },
};

const CATEGORIES = [
  { value: 'plannen', label: 'Plannen' },
  { value: 'schemas', label: "Schema's" },
  { value: 'offerte', label: 'Offerte' },
  { value: 'keuring', label: 'Keuring' },
  { value: 'facturen', label: 'Facturen' },
  { value: 'fotos', label: "Foto's" },
  { value: 'fotos_reiniging', label: "Foto's Reiniging" },
  { value: 'fotos_installatie', label: "Foto's Installatie" },
  { value: 'datasheets', label: 'Datasheets' },
  { value: 'andere', label: 'Andere documenten' },
];

const fmtSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtDate = (iso, locale = 'nl-BE') => {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' });
};

const extOf = (name) => (name?.split('.').pop() || '').toUpperCase().slice(0, 4);

const Thumb = ({ file, token }) => {
  const [pdfThumbFailed, setPdfThumbFailed] = useState(false);
  const ct = file.content_type || '';
  if (ct.startsWith('image/')) {
    return (
      <img
        src={`${API}/public/share/${token}/files/${file.id}`}
        alt={file.filename}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    );
  }
  if (ct === 'application/pdf' && !pdfThumbFailed) {
    return (
      <img
        src={`${API}/public/share/${token}/files/${file.id}/thumbnail`}
        alt={file.filename}
        loading="lazy"
        onError={() => setPdfThumbFailed(true)}
        className="w-full h-full object-cover object-top"
      />
    );
  }
  let Icon = File;
  let color = 'text-slate-400';
  let bg = 'bg-slate-100';
  if (ct === 'application/pdf') { Icon = FileText; color = 'text-red-500'; bg = 'bg-red-50'; }
  else if (ct.includes('sheet') || ct.includes('excel') || ct.includes('csv')) { Icon = FileSpreadsheet; color = 'text-emerald-600'; bg = 'bg-emerald-50'; }
  else if (ct.includes('zip') || ct.includes('compressed')) { Icon = FileArchive; color = 'text-amber-600'; bg = 'bg-amber-50'; }
  else if (ct.includes('word') || ct.includes('text')) { Icon = FileText; color = 'text-blue-500'; bg = 'bg-blue-50'; }
  return (
    <div className={`w-full h-full flex flex-col items-center justify-center gap-2 ${bg}`}>
      <Icon className={`w-12 h-12 ${color}`} strokeWidth={1.5} />
      <span className={`text-xs font-bold tracking-wider ${color}`}>{extOf(file.filename)}</span>
    </div>
  );
};

const SignaturePad = ({ onChange, t }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = 170 * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.4;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1e293b';
  }, []);

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };
  const down = (e) => {
    e.preventDefault();
    canvasRef.current.setPointerCapture(e.pointerId);
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const p = pos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    onChange(canvasRef.current.toDataURL('image/png'));
  };
  const up = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.restore();
    onChange(null);
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerLeave={up}
        style={{ touchAction: 'none' }}
        className="w-full h-[170px] bg-white border-2 border-dashed border-slate-300 rounded-xl cursor-crosshair"
        data-testid="signature-canvas"
      />
      <div className="flex items-center justify-between mt-1.5">
        <p className="text-xs text-slate-400">{t.drawHint}</p>
        <button type="button" onClick={clear}
          className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors"
          data-testid="signature-clear-btn">
          <RotateCcw className="w-3 h-3" /> {t.clear}
        </button>
      </div>
    </div>
  );
};

const SignModal = ({ file, token, onClose, onSigned, t }) => {
  const [name, setName] = useState('');
  const [sigData, setSigData] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = name.trim().length >= 2 && sigData && agreed && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API}/public/share/${token}/files/${file.id}/sign`, {
        name: name.trim(),
        signature_data: sigData,
        agreed: true
      });
      onSigned(res.data.signed_file);
    } catch (err) {
      setError(err.response?.data?.detail || t.signError);
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4 overflow-y-auto" data-testid="sign-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 pb-0">
          <div>
            <h2 className="text-xl font-bold text-slate-900">{t.signTitle}</h2>
            <p className="text-sm text-slate-500 mt-1 break-all">{file.filename}</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 p-1 rounded-lg transition-colors" data-testid="sign-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t.yourName}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.namePh}
              className="w-full border border-slate-300 rounded-xl px-4 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              data-testid="sign-name-input"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1.5">{t.signature}</label>
            <SignaturePad onChange={setSigData} t={t} />
          </div>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 w-4 h-4 accent-orange-600" data-testid="sign-agree-checkbox" />
            <span className="text-sm text-slate-600">
              {t.agree}
            </span>
          </label>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5" data-testid="sign-error">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-slate-200 hover:bg-slate-50 text-slate-700 font-medium rounded-xl px-4 py-2.5 transition-colors"
              data-testid="sign-cancel-btn">
              {t.cancel}
            </button>
            <button type="button" onClick={submit} disabled={!canSubmit}
              className="flex-1 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-xl px-4 py-2.5 transition-colors"
              data-testid="sign-submit-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PenLine className="w-4 h-4" />}
              {t.signBtn}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SharePublic() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [signFile, setSignFile] = useState(null);
  const [signedResult, setSignedResult] = useState(null);
  const [cleaningRequesting, setCleaningRequesting] = useState(false);
  const [cleaningRequestMsg, setCleaningRequestMsg] = useState(null);
  const [lang, setLang] = useState('nl');
  const [selectedProject, setSelectedProject] = useState(() => {
    const p = parseInt(new URLSearchParams(window.location.search).get('project'), 10);
    return Number.isInteger(p) && p > 0 ? p : null;
  });
  const t = SHARE_T[lang];

  const handleRequestCleaning = async () => {
    setCleaningRequesting(true);
    try {
      const res = await axios.post(`${API}/public/share/${token}/request-cleaning`);
      setCleaningRequestMsg(res.data.message);
    } catch (err) {
      setCleaningRequestMsg(t.cleaningError);
    } finally {
      setCleaningRequesting(false);
    }
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setLightbox(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const fetchData = useCallback(() => {
    return axios.get(`${API}/public/share/${token}`)
      .then(res => {
        setData(res.data);
        if (['nl', 'fr', 'en'].includes(res.data.language)) setLang(res.data.language);
      })
      .catch(err => setError(err.response?.data?.detail || 'Deze link is niet (meer) geldig'));
  }, [token]);

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleSigned = (signedFile) => {
    setSignFile(null);
    setLightbox(null);
    setSignedResult(signedFile);
    fetchData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-14 h-14 text-orange-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{t.invalidTitle}</h1>
          <p className="text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  const { folder, files: allFiles, company } = data;
  const projectCount = folder.project_count || 1;
  const activeProject = selectedProject && selectedProject <= projectCount ? selectedProject : projectCount;
  const files = projectCount > 1 ? allFiles.filter(f => (f.project || 1) === activeProject) : allFiles;
  const grouped = CATEGORIES.map(c => ({ ...c, label: t.cats[c.value] || c.label, items: files.filter(f => f.category === c.value) })).filter(g => g.items.length > 0);
  const isSignable = (f) => f.signable && !f.signed && f.content_type === 'application/pdf';

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col" data-testid="share-public-page">
      {/* Topbar */}
      <header className="border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          {company.logo_url ? (
            <img src={company.logo_url} alt={company.name} className="h-16 sm:h-20 object-contain" data-testid="share-company-logo" />
          ) : (
            <span className="font-bold text-lg">{company.name}</span>
          )}
          {/* Reiniging CTA in topbar */}
          <div className="hidden sm:flex flex-col md:flex-row items-center gap-2 md:gap-3 mx-4" data-testid="cleaning-cta">
            <p className="text-sm font-semibold text-slate-800">{t.ctaText}</p>
            {cleaningRequestMsg ? (
              <p className="text-sm font-medium text-emerald-600 flex items-center gap-1.5" data-testid="cleaning-request-result">
                <CheckCircle2 className="w-4 h-4" /> {cleaningRequestMsg}
              </p>
            ) : (
              <button type="button"
                onClick={handleRequestCleaning}
                disabled={cleaningRequesting}
                className="inline-flex items-center gap-2 text-sm font-semibold bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white rounded-full px-5 py-2 shadow-sm transition-colors"
                data-testid="request-cleaning-btn">
                {cleaningRequesting ? t.ctaSending : t.ctaBtn}
              </button>
            )}
          </div>
          <div className="hidden md:flex items-center gap-5">
            <div className="flex flex-col items-start gap-1 text-sm text-slate-500">
              {company.phone && <a href={`tel:${company.phone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 hover:text-orange-600"><Phone className="w-4 h-4" />{company.phone}</a>}
              {company.email && <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-orange-600"><Mail className="w-4 h-4" />{company.email}</a>}
              {company.website && (
                <a href={`https://${company.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-orange-600" data-testid="share-company-website">
                  <Globe className="w-4 h-4" />{company.website.replace(/^https?:\/\//, '')}
                </a>
              )}
            </div>
            <div className="flex items-center rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="share-lang-switch">
              {['nl', 'fr', 'en'].map((l) => (
                <button key={l} type="button" onClick={() => setLang(l)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wide transition-all ${lang === l
                    ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:text-slate-800'}`}
                  data-testid={`share-lang-${l}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="md:hidden flex items-center rounded-full border border-slate-200 bg-slate-50 p-1" data-testid="share-lang-switch-mobile">
            {['nl', 'fr', 'en'].map((l) => (
              <button key={l} type="button" onClick={() => setLang(l)}
                className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${lang === l ? 'bg-slate-900 text-white' : 'text-slate-500'}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6 py-12 text-center">
          <p className="text-orange-600 font-semibold tracking-widest uppercase text-xs mb-3">{t.heroKicker}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2" data-testid="share-folder-name">{folder.name}</h1>
          <p className="text-slate-500 mt-3 max-w-2xl mx-auto">
            {t.heroWelcome(company.name.replace(/\s*BV$/i, ''))}
          </p>
          {projectCount > 1 && (
            <div className="flex flex-wrap items-center justify-center gap-2 mt-6" data-testid="share-project-switch">
              {Array.from({ length: projectCount }, (_, i) => i + 1).map(n => (
                <button key={n} type="button" onClick={() => setSelectedProject(n)}
                  className={`px-6 py-2.5 rounded-full text-sm font-bold border-2 transition-all ${activeProject === n
                    ? 'bg-orange-600 border-orange-600 text-white shadow-md'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-orange-400 hover:text-orange-600'}`}
                  data-testid={`share-project-${n}`}>
                  {t.project} {n}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
            <div className="inline-flex items-center gap-2 text-sm text-slate-500 bg-white border border-slate-200 rounded-full px-4 py-1.5 shadow-sm">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              {t.secured(company.name)}
            </div>
            {files.length > 1 && (
              <a href={`${API}/public/share/${token}/download-all${projectCount > 1 ? `?project=${activeProject}` : ''}`}
                className="inline-flex items-center gap-2 text-sm font-semibold bg-orange-600 hover:bg-orange-500 text-white rounded-full px-5 py-1.5 shadow-sm transition-colors"
                data-testid="download-all-btn">
                <Download className="w-4 h-4" />
                {t.downloadAll(files.length)}
              </a>
            )}
          </div>
        </div>
      </div>
      <main className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        {files.length === 0 ? (
          <div className="text-center py-16 text-slate-400">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            {t.noDocs}
          </div>
        ) : (
          <div className="space-y-12">
            {grouped.map(group => (
              <section key={group.value}>
                <div className="flex items-center gap-3 mb-5">
                  <h2 className="text-lg font-bold text-slate-900">{group.label}</h2>
                  <span className="text-xs font-medium text-slate-500 bg-slate-100 rounded-full px-2.5 py-0.5">{group.items.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {group.items.map(f => (
                    <div key={f.id}
                      className={`group bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-200 ${isSignable(f) ? 'border-orange-300 ring-1 ring-orange-200' : 'border-slate-200 hover:border-orange-300'}`}
                      data-testid={`share-file-${f.id}`}>
                      <button type="button"
                        onClick={() => setLightbox(f)}
                        className="relative block w-full aspect-[4/3] overflow-hidden bg-slate-50 border-b border-slate-100 cursor-zoom-in"
                        data-testid={`share-thumb-${f.id}`}>
                        <Thumb file={f} token={token} />
                        {f.signed && (
                          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-600 text-white rounded-full px-2.5 py-1 shadow" data-testid={`signed-label-${f.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> {t.signed}
                          </span>
                        )}
                        {isSignable(f) && (
                          <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-orange-600 text-white rounded-full px-2.5 py-1 shadow" data-testid={`signable-label-${f.id}`}>
                            <PenLine className="w-3 h-3" /> {t.signRequired}
                          </span>
                        )}
                      </button>
                      <div className="p-4">
                        <p className="font-semibold text-sm text-slate-900 truncate" title={f.filename}>{f.filename}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{fmtSize(f.size)} · {fmtDate(f.uploaded_at, t.locale)}</p>
                        {f.signed && f.signed_by_name && (
                          <p className="text-xs text-emerald-600 font-medium mt-1">{t.signedBy(f.signed_by_name, fmtDate(f.signed_at, t.locale))}</p>
                        )}
                        <div className="flex gap-2 mt-3">
                          <button type="button" onClick={() => setLightbox(f)}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 px-3 py-2 rounded-lg transition-colors">
                            <Eye className="w-3.5 h-3.5" /> {t.view}
                          </button>
                          <a href={`${API}/public/share/${token}/files/${f.id}?download=true`}
                            className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-orange-600 hover:bg-orange-500 text-white px-3 py-2 rounded-lg transition-colors"
                            data-testid={`share-download-${f.id}`}>
                            <Download className="w-3.5 h-3.5" /> {t.download}
                          </a>
                        </div>
                        {isSignable(f) && (
                          <button type="button" onClick={() => setSignFile(f)}
                            className="w-full mt-2 inline-flex items-center justify-center gap-1.5 text-xs font-semibold bg-slate-900 hover:bg-slate-700 text-white px-3 py-2.5 rounded-lg transition-colors"
                            data-testid={`sign-btn-${f.id}`}>
                            <PenLine className="w-3.5 h-3.5" /> {t.placeSignature}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Lightbox popup */}
      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 sm:p-8" onClick={() => setLightbox(null)} data-testid="share-lightbox">
          <div className="relative w-full max-w-5xl max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="text-white font-medium truncate">{lightbox.filename}</p>
              <div className="flex items-center gap-2 shrink-0">
                {isSignable(lightbox) && (
                  <button type="button" onClick={() => setSignFile(lightbox)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold bg-white hover:bg-slate-100 text-slate-900 px-4 py-2 rounded-lg transition-colors"
                    data-testid="lightbox-sign-btn">
                    <PenLine className="w-4 h-4" /> {t.placeSignature}
                  </button>
                )}
                <a href={`${API}/public/share/${token}/files/${lightbox.id}?download=true`}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold bg-orange-600 hover:bg-orange-500 text-white px-4 py-2 rounded-lg transition-colors">
                  <Download className="w-4 h-4" /> {t.download}
                </a>
                <button type="button" onClick={() => setLightbox(null)}
                  className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-lg p-2 transition-colors"
                  data-testid="lightbox-close-btn" title="Sluiten (Esc)">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 min-h-0 rounded-xl overflow-hidden bg-white flex items-center justify-center">
              {lightbox.content_type?.startsWith('image/') ? (
                <img src={`${API}/public/share/${token}/files/${lightbox.id}`} alt={lightbox.filename}
                  className="max-w-full max-h-[80vh] object-contain" />
              ) : lightbox.content_type === 'application/pdf' ? (
                <iframe src={`${API}/public/share/${token}/files/${lightbox.id}`} title={lightbox.filename}
                  className="w-full h-[80vh] border-0" />
              ) : (
                <div className="py-24 px-8 text-center">
                  <File className="w-14 h-14 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600 font-medium mb-1">{lightbox.filename}</p>
                  <p className="text-slate-400 text-sm">{t.cantPreview}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Onderteken modal */}
      {signFile && (
        <SignModal file={signFile} token={token} onClose={() => setSignFile(null)} onSigned={handleSigned} t={t} />
      )}

      {/* Succes na ondertekenen */}
      {signedResult && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid="sign-success-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-9 h-9 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">{t.thanks}</h2>
            <p className="text-slate-500 mb-6">
              {t.signSuccess}
            </p>
            <div className="flex flex-col gap-2">
              <a href={`${API}/public/share/${token}/files/${signedResult.id}?download=true`}
                className="inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl px-4 py-2.5 transition-colors"
                data-testid="download-signed-btn">
                <Download className="w-4 h-4" /> {t.downloadSigned}
              </a>
              <button type="button" onClick={() => setSignedResult(null)}
                className="text-sm font-medium text-slate-500 hover:text-slate-800 py-2 transition-colors"
                data-testid="sign-success-close">
                {t.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-100 mt-auto">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="text-sm text-center sm:text-left">
            <p className="font-semibold text-slate-900">{company.name}</p>
            <p className="text-slate-500">{t.footerQ}</p>
          </div>
          <div className="flex flex-wrap justify-center gap-5 text-sm text-slate-600">
            {company.phone && <a href={`tel:${company.phone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors"><Phone className="w-4 h-4" />{company.phone}</a>}
            {company.email && <a href={`mailto:${company.email}`} className="flex items-center gap-1.5 hover:text-orange-600 transition-colors"><Mail className="w-4 h-4" />{company.email}</a>}
            {company.website && <a href={`https://${company.website.replace(/^https?:\/\//, '')}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-orange-600 transition-colors"><Globe className="w-4 h-4" />{company.website}</a>}
          </div>
        </div>
      </footer>
    </div>
  );
}
